import { theme } from '@mi-front/ui';
import { alpha, createTheme } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    /**
     * Sidenav surface colors. Shared shape for dark-rail and true-light themes;
     * `surface` selects chrome (scrollbars, separators).
     */
    sidenav: {
      surface: 'dark' | 'light';
      background: string;
      backgroundAppDark: string;
      text: string;
      /** Active leaf label (stronger than inactive `text`). */
      activeText: string;
      /** Ribbons, dots, and active icons. */
      accent: string;
      hover: string;
      icon: string;
      border: string;
      separator: string;
      /** Initials avatar when the user has no photo. */
      avatarFallback: string;
    };
  }

  interface PaletteOptions {
    sidenav?: {
      surface?: 'dark' | 'light';
      background?: string;
      backgroundAppDark?: string;
      text?: string;
      activeText?: string;
      accent?: string;
      hover?: string;
      icon?: string;
      border?: string;
      separator?: string;
      avatarFallback?: string;
    };
  }
}

/** Sidenav text — matches dark theme text.primary on the dark rail. */
const sidenavTextColor = '#cdd6f4';

/** App primary cyan — dark-rail sidenav accent matches this. */
const primaryMain = '#1cc0fc';

/** Checked switch track — darker primary variant (same as contained buttons) for contrast with the white thumb. */
const switchOnTrackColor = '#03a6e2';

/** Brand teal used for avatar initials and related chrome. */
const sidenavAvatarFallback = '#0e667f';

/** Unchecked switch track — same grey as input borders so switches read as part of the form. */
const switchOffTrackColor = '#ced4da';

/** Dark-rail sidenav palette (used by mixed light app + dark app themes). */
const sidenavPalette = {
  surface: 'dark' as const,
  background: theme.blue,
  backgroundAppDark: '#0e1120',
  text: sidenavTextColor,
  activeText: '#ffffff',
  accent: primaryMain,
  hover: alpha(sidenavTextColor, 0.06),
  icon: '#e9ecef',
  border: 'transparent',
  separator: 'rgba(255, 255, 255, 0.15)',
  avatarFallback: sidenavAvatarFallback,
};

/**
 * True-light sidenav: soft gray rail with medium-dark inactive chrome.
 * `activeText` + `accent` carry active-leaf contrast on this surface.
 */
const lightSidenavPalette = {
  surface: 'light' as const,
  background: '#f4f5f7',
  backgroundAppDark: '#f4f5f7',
  // Tailwind gray-700 — solid enough to read, leaves room for active near-black.
  text: '#374151',
  // Tailwind gray-900 — anchors the current location against inactive text.
  activeText: '#111827',
  // Deeper teal so ribbons/dots pop on soft gray (same as switchOnTrackColor).
  accent: switchOnTrackColor,
  // Sheer brand tint so hover feels alive without a heavy gray wash.
  hover: alpha(switchOnTrackColor, 0.06),
  icon: '#374151',
  // Tailwind gray-200 — clear rail edge against the app canvas.
  border: '#e5e7eb',
  separator: '#e5e7eb',
  avatarFallback: sidenavAvatarFallback,
};

export const lightTheme = createTheme({
  typography: {
    /** Uses the same variable as ui.css so FontSwitcher can change typography globally. */
    fontFamily: 'var(--font-family-sans-serif)',
  },
  palette: {
    primary: {
      main: primaryMain,
      contrastText: '#fff',
    },
    secondary: {
      main: '#6c757d', // grey
      contrastText: '#fff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.3)',
    },
    background: {
      default: '#fcfcfc',
      paper: '#ffffff',
    },
    sidenav: sidenavPalette,
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiGrid: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiGrid2: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: 2,
        useFlexGap: true,
      },
    },
    /** Inline links underline on hover; matches existing call-site convention. */
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiButton: {
      styleOverrides: {
        /**
         * When Button is rendered as <a> (e.g. ButtonLink), Bootstrap's `a:hover`
         * (specificity 0,1,1) overrides the emotion class color (0,1,0) and forces
         * dark-blue link text — unreadable on the filled primary background.
         * Re-assert contrastText on hover so the more specific `.class:hover` wins.
         */
        contained: ({ theme, ownerState }) => {
          const paletteColor =
            ownerState.color !== 'inherit' && ownerState.color !== undefined
              ? theme.palette[ownerState.color]
              : undefined;
          const contrastText =
            paletteColor && 'contrastText' in paletteColor
              ? paletteColor.contrastText
              : theme.palette.primary.contrastText;
          return {
            '&:hover': {
              color: contrastText,
            },
          };
        },
        containedPrimary: {
          backgroundColor: '#03a6e2', // Darker variant for better contrast with white text
          '&:hover': {
            backgroundColor: '#0394c9', // Even darker on hover
            color: '#fff',
          },
        },
        outlinedPrimary: {
          borderColor: '#03a6e2', // Use darker variant for border
          color: '#03a6e2', // Use darker variant for text
          '&:hover': {
            borderColor: '#0394c9',
            backgroundColor: 'rgba(3, 166, 226, 0.04)', // Light background on hover
            color: '#0394c9', // Keep primary text; Bootstrap a:hover would otherwise win
          },
        },
        /**
         * Same Bootstrap `a:hover` fight as outlinedPrimary: ButtonLink renders as <a>,
         * so re-assert secondary text color on hover (DataToolbar uses <button>, so it
         * never hits this path).
         */
        outlinedSecondary: ({ theme }) => ({
          '&:hover': {
            color: theme.palette.secondary.main,
          },
        }),

        containedSuccess: {
          '&:hover': {
            backgroundColor: '#009966', // Slightly darker than success.main for hover
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        text: {
          fill: 'white',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingBottom: 16,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: '38px',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          '& fieldset': {
            borderColor: '#ced4da',
            borderRadius: '0.375rem',
          },
          '&:hover fieldset': {
            borderColor: '#ced4da',
          },
          '&.Mui-focused fieldset': {
            // borderColor: '#9ae3fe',
            // boxShadow: '0 0 0 0.2rem rgba(28, 192, 252, 0.25)',
          },
          // '&.Mui-disabled fieldset': {
          //   borderColor: '#ced4da',
          //   backgroundColor: '#e9ecef',
          // },
          '&.Mui-error fieldset': {
            borderColor: '#dc3545',
          },
          '&.Mui-error:hover fieldset': {
            borderColor: '#dc3545',
          },
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: '#dc3545',
            // boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          padding: '0rem 0.25rem',
          backgroundColor: '#ffffff',
        },
        input: {
          color: '#343a40',
          backgroundColor: '#ffffff',
          '&::placeholder': {
            color: '#808080',
            opacity: 1,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme }) => ({
          // Reserve space so the selected indicator does not shift the option
          borderLeft: '3px solid transparent',
          '&[aria-selected="true"]': {
            borderLeftColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover ::after': {
            backgroundColor: 'rgba(178, 178, 178, 0.15) !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          textTransform: 'none',
        },
      },
    },
    /**
     * MRT’s rows-per-page control uses className "MuiTablePagination-root" with an
     * InputLabel + Select (not MUI TablePagination slots). Use body2 (same as MUI’s
     * native TablePagination) instead of the larger default InputLabel/Select size.
     */
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiTablePagination-root &': {
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          '.MuiTablePagination-root &': {
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
          },
        }),
      },
    },
    /** Filled variant uses getContrastText() instead of palette[severity].contrastText;
     * override so all filled Alerts use the theme contrast text for readability. */
    MuiAlert: {
      styleOverrides: {
        filledSuccess: ({ theme }) => ({
          color: theme.palette.success.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
        filledError: ({ theme }) => ({
          color: theme.palette.error.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
        filledWarning: ({ theme }) => ({
          color: theme.palette.warning.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
        filledInfo: ({ theme }) => ({
          color: theme.palette.info.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === 'filled' &&
            ownerState.color === 'default' && {
              backgroundColor: '#ebebeb',
            }),
        }),
        filledPrimary: {
          color: '#fff', // force white text
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          // paddingTop: 0,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0, // Globally normalize alignment
          // The compact switch has no padding around its track, so the gap to the label
          // (which checkboxes and radios get from their own padding) is added here.
          '& .MuiSwitch-root + .MuiFormControlLabel-label': {
            marginLeft: 8,
          },
        },
      },
    },
    /**
     * Compact flat switch replacing the Material 2 default: solid tracks instead of translucent
     * ones, and an off-state grey taken from the input borders. Sizing is absolute (not derived
     * from the default 58x38 box) so the control lines up with the form density.
     * - medium (default): 40×22, 16px thumb — form rows / SwitchCard
     * - small: 32×18, 14px thumb — dense cards like PublishToggleCard
     */
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          width: 40,
          height: 22,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible', // Lets the focus ring around the track be painted
        },
        /**
         * Dense size. MUI's built-in small re-declares geometry via descendant selectors that
         * outrank switchBase/thumb overrides, so every dimension is reset here.
         */
        sizeSmall: {
          width: 32,
          height: 18,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(14px)',
            },
          },
          '& .MuiSwitch-thumb': {
            width: 14,
            height: 14,
          },
          '& .MuiSwitch-track': {
            borderRadius: 9,
          },
        },
        switchBase: {
          padding: 3,
          color: '#fff', // thumb color: the thumb paints itself with currentColor
          // The ripple is disabled, so its hover circle would only overflow the smaller track.
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
          },
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 1,
          },
          '&.MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
            backgroundColor: switchOnTrackColor,
          },
          // Keyboard focus reuses the focus glow of the inputs, since there is no ripple.
          '&.Mui-focusVisible + .MuiSwitch-track': {
            boxShadow: '0 0 0 3px rgba(28, 192, 252, 0.25)',
          },
          '&.Mui-disabled': {
            color: '#fff',
            '& + .MuiSwitch-track': {
              opacity: 0.4,
            },
            '& .MuiSwitch-thumb': {
              boxShadow: 'none',
            },
          },
        },
        thumb: {
          width: 16,
          height: 16,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
        },
        track: {
          borderRadius: 11,
          opacity: 1,
          backgroundColor: switchOffTrackColor,
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.body2,
          lineHeight: '36px',
          typography: 'caption',
          // Sticky subheaders use flat background.paper; Menu/Popover Paper adds
          // --Paper-overlay (elevation). Inherit that overlay so the shade matches.
          '&.MuiListSubheader-sticky': {
            backgroundImage: 'var(--Paper-overlay)',
          },
        }),
      },
    },
    // MuiListItem: {
    //   styleOverrides: {
    //     root: ({ theme }) => ({
    //       borderRadius: theme.shape.borderRadius,
    //     }),
    //   },
    // },
    // MuiListItemButton: {
    //   styleOverrides: {
    //     root: ({ theme }) => ({
    //       borderRadius: theme.shape.borderRadius,
    //     }),
    //   },
    // },
    MuiFormHelperText: {
      defaultProps: {
        component: 'div',
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 500,
      },
      styleOverrides: {
        tooltip: {
          fontWeight: 400,
          backgroundColor: alpha(theme.blue, 0.8),
        },
      },
    },
  },
});

/**
 * Light app canvas with a light sidenav (true light theme).
 * Inherits all lightTheme component overrides; only sidenav palette changes.
 */
export const lightFullTheme = createTheme(lightTheme, {
  palette: {
    sidenav: lightSidenavPalette,
  },
});

export const darkTheme = createTheme(lightTheme, {
  palette: {
    mode: 'dark',
    background: {
      default: '#13151f',
      paper: '#1a1d2e',
    },
    secondary: {
      main: '#adb5bd', // light gray — visible on dark surfaces (base is #6c757d, too dim)
      contrastText: '#13151f',
    },
    text: {
      primary: sidenavTextColor,
      secondary: '#8892b0',
      disabled: 'rgba(205, 214, 244, 0.38)',
    },
    action: {
      // Explicit overrides: MUI may inherit light-mode action colors from the base
      // muiTheme even with mode:'dark', making IconButton icons appear dark.
      active: 'rgba(205, 214, 244, 0.7)',
      hover: 'rgba(205, 214, 244, 0.08)',
      selected: 'rgba(205, 214, 244, 0.16)',
      disabled: 'rgba(205, 214, 244, 0.38)',
      disabledBackground: 'rgba(205, 214, 244, 0.12)',
    },
    divider: '#3a4560',
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1d2e',
        },
        input: {
          color: '#cdd6f4',
          backgroundColor: '#1a1d2e',
          '&::placeholder': {
            color: '#8892b0',
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#2a2d3e',
            borderRadius: '0.375rem',
          },
          '&:hover fieldset': {
            borderColor: '#3a3f5c',
          },
          '&.Mui-error fieldset': {
            borderColor: '#dc3545',
          },
          '&.Mui-error:hover fieldset': {
            borderColor: '#dc3545',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        root: ({ ownerState }: any) => ({
          ...(ownerState.variant === 'filled' &&
            ownerState.color === 'default' && {
              backgroundColor: '#1e2035',
            }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1d2e',
          borderColor: '#2a2d3e',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover ::after': {
            backgroundColor: 'rgba(255, 255, 255, 0.04) !important',
          },
        },
      },
    },
    /** Only the off-state track changes: the light grey is invisible on dark surfaces. */
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: '#3a3f5c',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: 'rgba(205, 214, 244, 0.6)',
          '&.Mui-selected': {
            color: '#cdd6f4',
          },
          '&.Mui-disabled': {
            color: 'rgba(205, 214, 244, 0.3)',
          },
        },
      },
    },
  },
});
